import { Client, type IMessage, type StompSubscription } from '@stomp/stompjs'

import { env, resolveWebSocketUrl } from '@/lib/env'

export type ConnectionStatus = 'idle' | 'connecting' | 'connected' | 'reconnecting' | 'error'

export type MessageHandler = (message: IMessage) => void
type StatusListener = (status: ConnectionStatus) => void

export interface StompConnectionOptions {
  /** Sent as a STOMP CONNECT header — Spring reads it in a channel interceptor. */
  getAccessToken?: () => string | null
  url?: string
}

/**
 * Wraps a STOMP client so React never touches subscription bookkeeping.
 *
 * Two things it buys us:
 *  - subscriptions can be registered before the socket is up (and survive a
 *    reconnect) — they are replayed on every CONNECTED frame;
 *  - many components can listen to one destination while only one STOMP
 *    subscription exists on the wire.
 */
export class StompConnection {
  private client: Client | null = null
  private readonly handlers = new Map<string, Set<MessageHandler>>()
  private readonly subscriptions = new Map<string, StompSubscription>()
  private readonly statusListeners = new Set<StatusListener>()
  private status: ConnectionStatus = 'idle'

  constructor(private readonly options: StompConnectionOptions = {}) {}

  getStatus(): ConnectionStatus {
    return this.status
  }

  onStatusChange(listener: StatusListener): () => void {
    this.statusListeners.add(listener)
    return () => this.statusListeners.delete(listener)
  }

  activate(): void {
    if (this.client) return

    const client = new Client({
      brokerURL: resolveWebSocketUrl(this.options.url),
      reconnectDelay: 4000,
      heartbeatIncoming: 10_000,
      heartbeatOutgoing: 10_000,
      debug: env.isDev ? (msg) => console.debug('[stomp]', msg) : undefined,
      beforeConnect: () => {
        const token = this.options.getAccessToken?.()
        client.connectHeaders = token ? { Authorization: `Bearer ${token}` } : {}
      },
      onConnect: () => {
        this.setStatus('connected')
        for (const destination of this.handlers.keys()) {
          this.openSubscription(destination)
        }
      },
      onWebSocketClose: () => {
        this.subscriptions.clear()
        this.setStatus(this.client ? 'reconnecting' : 'idle')
      },
      onStompError: (frame) => {
        console.error('[stomp] broker error:', frame.headers.message, frame.body)
        this.setStatus('error')
      },
    })

    this.client = client
    this.setStatus('connecting')
    client.activate()
  }

  async deactivate(): Promise<void> {
    const client = this.client
    this.client = null
    this.subscriptions.clear()
    this.setStatus('idle')
    await client?.deactivate()
  }

  /** Registers a handler and returns its unsubscribe. Safe to call while offline. */
  subscribe(destination: string, handler: MessageHandler): () => void {
    const handlers = this.handlers.get(destination) ?? new Set<MessageHandler>()
    handlers.add(handler)
    this.handlers.set(destination, handlers)

    if (this.client?.connected && !this.subscriptions.has(destination)) {
      this.openSubscription(destination)
    }

    return () => {
      const current = this.handlers.get(destination)
      current?.delete(handler)

      if (current && current.size === 0) {
        this.handlers.delete(destination)
        this.subscriptions.get(destination)?.unsubscribe()
        this.subscriptions.delete(destination)
      }
    }
  }

  /** Same as `subscribe`, with the JSON body already parsed. */
  subscribeJson<T>(destination: string, handler: (payload: T) => void): () => void {
    return this.subscribe(destination, (message) => {
      try {
        handler(JSON.parse(message.body) as T)
      } catch (error) {
        console.error(`[stomp] unparseable payload on ${destination}`, error)
      }
    })
  }

  publish(destination: string, body: unknown): void {
    if (!this.client?.connected) {
      console.warn(`[stomp] dropped a message for ${destination} — not connected`)
      return
    }
    this.client.publish({ destination, body: JSON.stringify(body) })
  }

  private openSubscription(destination: string): void {
    if (!this.client?.connected) return

    const subscription = this.client.subscribe(destination, (message) => {
      for (const handler of this.handlers.get(destination) ?? []) {
        handler(message)
      }
    })
    this.subscriptions.set(destination, subscription)
  }

  private setStatus(status: ConnectionStatus): void {
    if (this.status === status) return
    this.status = status
    for (const listener of this.statusListeners) listener(status)
  }
}
